import { compareTwoStrings as defaultCompareTwoStrings } from "../SharedTransforms";

const DEFAULT_MATCH_THRESHOLD = 0.4;
// Index-based fallback should only apply when headings are still reasonably close.
// A lower threshold can incorrectly transfer settings (e.g. `show: false`) to unrelated sections.
const INDEX_POSITION_MATCH_THRESHOLD = 0.4;
const SPECIAL_SECTION_KEYS = new Set(["segment_title", "vertical_sections"]);
const normalizeHeading = (heading) => (heading ?? "").toLowerCase().trim();
const COMPANY_LOGO_KEYWORD_RE = /(logo|logo wall|logo grid|brand logo|brand mark|badge wall|badge list)/;
const COMPANY_LOGO_TARGETS = [
  "company logos",
  "logo wall",
  "logo grid",
  "logos",
  "partner logos",
  "brand logos",
  "brand marks",
  "badge wall",
];

const looksLikeCompanyLogoHeading = (heading) => {
  const normalized = normalizeHeading(heading);
  if (!normalized) return false;
  return COMPANY_LOGO_KEYWORD_RE.test(normalized);
};

const SECTION_STYLE_MATCHERS = [
  { name: "title", targets: ["title", "analysis title", "summary title"] },
  { name: "summary", targets: ["description", "summary", "overview"] },
  { name: "detailsBullets", targets: ["recurring topics", "themes", "topics", "capabilities"] },
  { name: "quotes", targets: ["quotes", "verbatim quotes", "examples", "evidence quotes", "customers"] },
  { name: "sentiment", targets: ["overall sentiment", "sentiment"] },
  {
    name: "company_logos",
    match: (heading, section, { compareFn }) => {
      if (!looksLikeCompanyLogoHeading(heading)) {
        return 0;
      }
      return scoreMatch(heading, COMPANY_LOGO_TARGETS, compareFn);
    },
  },
  { name: "organizations", targets: ["organizations", "partners"] },
  { name: "paragraphFallback", fallback: true, match: () => 0.05 },
];

function cloneConfigEntry(entry) {
  if (!entry || typeof entry !== "object") {
    return {};
  }
  return { ...entry };
}

function pickBestConfigKey(heading, configEntries, compareFn) {
  if (!heading) {
    return undefined;
  }
  let bestScore = -1;
  let bestKey;
  for (const [key] of configEntries) {
    if (!key) continue;
    let score = 0;
    if (typeof compareFn === "function") {
      score = compareFn(key, heading);
    } else if (key === heading) {
      score = 1;
    }
    if (score > bestScore && score >= DEFAULT_MATCH_THRESHOLD) {
      bestScore = score;
      bestKey = key;
    }
  }
  return bestKey;
}

function scoreMatch(heading, targets, compareFn) {
  const h = normalizeHeading(heading);
  if (!h) return 0;
  const list = [targets].flat().map(normalizeHeading).filter(Boolean);
  let best = 0;
  for (const target of list) {
    if (!target) continue;
    if (h === target) {
      return 1;
    }
    if (h.includes(target) || target.includes(h)) {
      best = Math.max(best, 0.85);
    }
    if (typeof compareFn === "function") {
      best = Math.max(best, compareFn(h, target));
    }
  }
  return best;
}

export function guessStructuredSectionStyle(section, { compareFn = defaultCompareTwoStrings } = {}) {
  const heading = section?.heading ?? section?.originalHeading ?? "";
  let best = { score: -1, name: undefined };
  for (const matcher of SECTION_STYLE_MATCHERS) {
    const score = matcher.match
      ? matcher.match(heading, section, { compareFn })
      : scoreMatch(heading, matcher.targets ?? [], compareFn);
    if (score > best.score) {
      best = { score, name: matcher.name, matcher };
    }
  }
  const fallback = SECTION_STYLE_MATCHERS.find((m) => m.fallback);
  if (best.name && best.score >= DEFAULT_MATCH_THRESHOLD) {
    return best.name;
  }
  return fallback?.name ?? best.name;
}

const getTemplateSectionHeading = (node) =>
  node?.heading ?? node?.title ?? node?.name ?? node?.label ?? node?.id ?? undefined;

const inferSectionStyleFromTemplate = (node, compareFn) => {
  if (!node || typeof node !== "object") return undefined;
  const explicit =
    node.sectionStyle ??
    node.style ??
    node.renderStyle ??
    node.render_mode ??
    node.mode ??
    node.presentation;
  if (explicit) return explicit;

  const type = String(node.type ?? node.response_type ?? node.content_type ?? "")
    .toLowerCase()
    .trim();
  if (type) {
    if (type.includes("quote")) return "quotes";
    if (type.includes("sentiment")) return "sentiment";
    if (COMPANY_LOGO_KEYWORD_RE.test(type)) return "company_logos";
    if (type.includes("organization")) return "organizations";
    if (type.includes("bullet") || type.includes("list")) return "detailsBullets";
    if (type.includes("title") || type.includes("headline")) return "title";
    if (type.includes("summary") || type.includes("overview")) return "summary";
    if (type.includes("segment")) return "segmentTitle";
  }

  const heading = getTemplateSectionHeading(node);
  if (heading) {
    return guessStructuredSectionStyle({ heading }, { compareFn });
  }
  return undefined;
};

const normalizeTemplateStructure = (structureTemplate, compareFn) => {
  const structureArray = Array.isArray(structureTemplate)
    ? structureTemplate
    : Array.isArray(structureTemplate?.structure)
    ? structureTemplate.structure
    : [];

  return structureArray
    .map((node, index) => {
      const heading = getTemplateSectionHeading(node);
      if (!heading && !Array.isArray(node?.subsections)) {
        return undefined;
      }
      const normalizedHeading = heading ? normalizeHeading(heading) : undefined;
      return {
        index,
        heading,
        normalizedHeading,
        sectionStyle: inferSectionStyleFromTemplate(node, compareFn),
        node,
      };
    })
    .filter(Boolean);
};

export function prepareStructuredSummary({
  structuredSummary,
  sectionConfig,
  segmentTitle,
  summaryFallback,
  includeSegmentTitle = false,
  structureTemplate,
  compareFn = defaultCompareTwoStrings,
} = {}) {
  const templateEntries = normalizeTemplateStructure(structureTemplate, compareFn);
  const templateMap = new Map(
    templateEntries
      .filter((entry) => entry.normalizedHeading)
      .map((entry) => [entry.normalizedHeading, entry])
  );

  const sections =
    (Array.isArray(structuredSummary) && structuredSummary.length > 0
      ? structuredSummary
      : summaryFallback
      ? [{ content: summaryFallback }]
      : []
    ).map((section) => ({ ...section }));

  // If a section has an exact heading key in config, reserve that key so
  // fuzzy/index matching cannot reassign it to other sections.
  const reservedExactConfigKeys = new Set(
    sections
      .map((section) => section?.heading)
      .filter((heading) => heading && sectionConfig?.[heading])
  );

  const resolvedConfig = Object.entries(sectionConfig ?? {}).reduce((acc, [key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      acc[key] = cloneConfigEntry(value);
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
  const configEntries = Object.entries(sectionConfig ?? {}).filter(
    ([key, value]) => !SPECIAL_SECTION_KEYS.has(key) && value && typeof value === "object"
  );

  templateEntries.forEach((entry) => {
    const { heading } = entry;
    if (!heading) return;
    if (!resolvedConfig[heading]) {
      resolvedConfig[heading] = {};
    }
    if (entry.sectionStyle != null && resolvedConfig[heading].sectionStyle == null) {
      resolvedConfig[heading].sectionStyle = entry.sectionStyle;
    }
  });

  let firstFontSize;

  const formattedSections = sections
    .map((section, index) => {
      const originalHeading = section.heading;
      const heading = section.heading;
      let configKey = heading && sectionConfig?.[heading] ? heading : undefined;
      let config = configKey ? sectionConfig?.[configKey] : undefined;
      const hasExactConfigMatch = Boolean(configKey);
      const availableConfigEntries = hasExactConfigMatch
        ? configEntries
        : configEntries.filter(([key]) => !reservedExactConfigKeys.has(key));

      if (!config && heading) {
        const matchedKey = pickBestConfigKey(heading, availableConfigEntries, compareFn);
        if (matchedKey) {
          configKey = matchedKey;
          config = sectionConfig?.[matchedKey];
        }
      }

      if (!config && heading) {
        const candidateEntry = availableConfigEntries[index];
        if (candidateEntry) {
          const [candidateKey, candidateConfig] = candidateEntry;
          if (candidateConfig && typeof candidateConfig === "object") {
            let score = 0;
            if (typeof compareFn === "function") {
              score = compareFn(candidateKey, heading);
            } else if (candidateKey === heading) {
              score = 1;
            }
            if (score >= INDEX_POSITION_MATCH_THRESHOLD) {
              configKey = candidateKey;
              config = candidateConfig;
            }
          }
        }
      }

      const clonedConfig = cloneConfigEntry(config);

      if (configKey) {
        resolvedConfig[configKey] = clonedConfig;
      }

      if (clonedConfig.show === false) {
        return undefined;
      }

      const formatted = { ...section, originalHeading, _originalIndex: index };
      if (configKey) {
        formatted.configKey = configKey;
      }

      if (typeof clonedConfig.headingText === "string") {
        const trimmedHeading = clonedConfig.headingText.trim();
        clonedConfig.headingText = trimmedHeading.length > 0 ? trimmedHeading : undefined;
      }

      const headingOverride =
        typeof clonedConfig.headingText === "string" && clonedConfig.headingText.trim().length > 0
          ? clonedConfig.headingText.trim()
          : undefined;

      if (clonedConfig.heading === false) {
        formatted.heading = undefined;
      } else if (headingOverride) {
        formatted.heading = headingOverride;
      }

      formatted.largeSpacing = clonedConfig.largeSpacing !== false;

      if ("fontSize" in clonedConfig) {
        formatted.fontSize = clonedConfig.fontSize;
        if (clonedConfig.fontSize != null && firstFontSize == null) {
          firstFontSize = clonedConfig.fontSize;
        }
      }

      if ("fontStyle" in clonedConfig) {
        formatted.fontStyle = clonedConfig.fontStyle;
      }

      if ("sectionStyle" in clonedConfig) {
        formatted.sectionStyle = clonedConfig.sectionStyle;
      }

      if ("itemCount" in clonedConfig) {
        formatted.itemCount = clonedConfig.itemCount;
      }

      if ("includeIcons" in clonedConfig) {
        formatted.includeIcons = clonedConfig.includeIcons;
      }

      const autoStyle = guessStructuredSectionStyle(formatted, { compareFn });
      if (autoStyle) {
        formatted.autoSectionStyle = autoStyle;
      }

      const normalizedHeading = heading ? normalizeHeading(heading) : undefined;
      let templateEntry = normalizedHeading ? templateMap.get(normalizedHeading) : undefined;
      if (!templateEntry && heading) {
        let bestEntry;
        let bestScore = -1;
        for (const entry of templateEntries) {
          if (!entry.heading) continue;
          const score = compareFn ? compareFn(heading, entry.heading) : 0;
          if (score > bestScore) {
            bestScore = score;
            bestEntry = entry;
          }
        }
        if (bestScore >= DEFAULT_MATCH_THRESHOLD) {
          templateEntry = bestEntry;
        }
      }

      if (templateEntry) {
        templateEntry.matched = true;
        formatted.templateEntry = templateEntry;
        formatted.templateKey = templateEntry.normalizedHeading ?? `template_${templateEntry.index}`;
        if (!formatted.configKey && templateEntry.heading) {
          formatted.configKey = templateEntry.heading;
        }
        if (!formatted.heading && templateEntry.heading) {
          formatted.heading = templateEntry.heading;
        }
        if (!formatted.sectionStyle && templateEntry.sectionStyle) {
          formatted.sectionStyle = templateEntry.sectionStyle;
        }
      }

      return formatted;
    })
    .filter(Boolean);

  if (includeSegmentTitle && segmentTitle && sectionConfig?.segment_title?.show) {
    const segmentConfig = sectionConfig.segment_title;
    formattedSections.unshift({
      content: segmentTitle,
      fontStyle: segmentConfig?.fontStyle ?? "bold",
      fontSize:
        segmentConfig?.fontSize ??
        (firstFontSize != null ? firstFontSize * 1.4 : undefined),
      sectionStart: true,
      configKey: "segment_title",
      sectionStyle: segmentConfig?.sectionStyle,
      autoSectionStyle: segmentConfig?.sectionStyle ?? "segmentTitle",
    });
  }

  if (sectionConfig?.segment_title) {
    resolvedConfig.segment_title = { ...sectionConfig.segment_title };
  }
  if (sectionConfig?.vertical_sections !== undefined) {
    resolvedConfig.vertical_sections = sectionConfig.vertical_sections;
  }

  const matchedSections = new Map(
    formattedSections
      .filter((section) => section.templateEntry)
      .map((section) => [
        section.templateEntry?.normalizedHeading ?? `template_${section.templateEntry?.index}`,
        section,
      ])
  );

  const orderedFromTemplate = templateEntries.map((entry) => {
    const key = entry.normalizedHeading ?? `template_${entry.index}`;
    const matched = matchedSections.get(key);
    if (matched) {
      return matched;
    }
    const heading = entry.heading ?? `Section ${entry.index + 1}`;
    const existingConfig = resolvedConfig[heading] ?? {};
    resolvedConfig[heading] = existingConfig;
    const configHeadingOverride =
      typeof existingConfig.headingText === "string" && existingConfig.headingText.trim().length > 0
        ? existingConfig.headingText.trim()
        : undefined;
    if (configHeadingOverride) {
      existingConfig.headingText = configHeadingOverride;
    }
    if (entry.sectionStyle != null && existingConfig.sectionStyle == null) {
      existingConfig.sectionStyle = entry.sectionStyle;
    }
    return {
      heading: configHeadingOverride ?? heading,
      content: "",
      templateOnly: true,
      sectionStyle: existingConfig.sectionStyle ?? entry.sectionStyle,
      largeSpacing: existingConfig.largeSpacing !== false,
      fontSize: existingConfig.fontSize,
      fontStyle: existingConfig.fontStyle,
      configKey: heading,
      templateEntry: entry,
      templateKey: key,
    };
  });

  const unmatchedSections = formattedSections.filter((section) => !section.templateEntry);
  const finalSections = [...orderedFromTemplate, ...unmatchedSections];

  return {
    sections: finalSections,
    sectionConfig: resolvedConfig,
    template: templateEntries,
  };
}

function extractTemplateFromCandidate(candidate) {
  if (!candidate) return undefined;
  const candidateConfig = candidate.getConfig ?? candidate.config;
  const revisedConfig =
    candidateConfig?.revised_query ??
    candidate?.referenceParameters?.revised_query ??
    candidateConfig?.summaryStructure;
  if (!revisedConfig) {
    return undefined;
  }
  const structure =
    Array.isArray(revisedConfig?.structure?.structure)
      ? revisedConfig.structure.structure
      : Array.isArray(revisedConfig?.structure)
      ? revisedConfig.structure
      : Array.isArray(revisedConfig?.summary)
      ? revisedConfig.summary
      : undefined;
  if (Array.isArray(structure) && structure.length > 0) {
    return structure;
  }
  return undefined;
}

export function resolveSummaryStructureTemplate(primitive, options = {}) {
  if (!primitive) {
    return { template: undefined, source: undefined };
  }
  const { includeParents = true } = options;
  const visited = new Set();
  const queue = [];

  const addCandidate = (candidate) => {
    if (!candidate) return;
    const key = candidate.id ?? candidate;
    if (visited.has(key)) return;
    visited.add(key);
    queue.push(candidate);
  };

  addCandidate(primitive);
  if (primitive.configParent) {
    addCandidate(primitive.configParent);
  }

  if (includeParents && typeof primitive.findParentPrimitives === "function") {
    try {
      const parents = primitive.findParentPrimitives() ?? [];
      parents.forEach(addCandidate);
    } catch (err) {
      console.warn("Unable to resolve parent primitives for template extraction", err);
    }
  }

  while (queue.length) {
    const candidate = queue.shift();
    const template = extractTemplateFromCandidate(candidate);
    if (template && template.length) {
      return { template, source: candidate };
    }
  }

  return { template: undefined, source: undefined };
}
